import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  FileText, Download, Trash2, Loader2, Home, Merge, Plus, 
  ChevronRight, ArrowLeft, FolderOpen, Calendar, HardDrive,
  CheckCircle2, AlertCircle, Clock, Globe, RefreshCw, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = window.location.origin;

export default function MyPDFs({ user, subjectSelection }) {
  const [storedPDFs, setStoredPDFs] = useState([]);
  const [pendingPDFs, setPendingPDFs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  
  // Merge dialog state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [availableTopics, setAvailableTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [merging, setMerging] = useState(false);
  const [customName, setCustomName] = useState("");
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [selectedMedium, setSelectedMedium] = useState("english");
  
  // Confirmation dialog for not-ready topics
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  
  // Polling ref for pending jobs
  const pollingRef = useRef(null);

  // Fetch stored PDFs
  const fetchStoredPDFs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notes/pdf/stored`, {
        params: { user_id: user.id }
      });
      setStoredPDFs(res.data.pdfs || []);
    } catch (err) {
      console.error("Failed to fetch stored PDFs:", err);
    }
  }, [user.id]);

  // Fetch pending PDFs
  const fetchPendingPDFs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notes/pdf/pending`, {
        params: { user_id: user.id }
      });
      setPendingPDFs(res.data.pending_pdfs || []);
    } catch (err) {
      console.error("Failed to fetch pending PDFs:", err);
    }
  }, [user.id]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStoredPDFs(), fetchPendingPDFs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStoredPDFs, fetchPendingPDFs]);

  // Poll for pending PDFs status
  useEffect(() => {
    if (pendingPDFs.length > 0) {
      pollingRef.current = setInterval(async () => {
        await fetchPendingPDFs();
        await fetchStoredPDFs();
      }, 5000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [pendingPDFs.length, fetchPendingPDFs, fetchStoredPDFs]);

  // Fetch topics with status for selected medium
  const fetchTopicsWithStatus = useCallback(async () => {
    if (!subjectSelection) return;
    
    setLoadingTopics(true);
    try {
      const res = await axios.get(`${API}/api/notes/topics-with-status`, {
        params: {
          exam_type: subjectSelection.category,
          subject: subjectSelection.subject,
          medium: selectedMedium
        }
      });
      setAvailableTopics(res.data.topics || []);
    } catch (err) {
      console.error("Failed to fetch topics:", err);
      toast.error("Failed to load topics");
    } finally {
      setLoadingTopics(false);
    }
  }, [subjectSelection, selectedMedium]);

  // Refetch topics when medium changes
  useEffect(() => {
    if (showMergeDialog && subjectSelection) {
      setSelectedTopics([]);
      fetchTopicsWithStatus();
    }
  }, [selectedMedium, showMergeDialog, subjectSelection, fetchTopicsWithStatus]);

  // Download stored PDF
  const handleDownload = async (pdf) => {
    setDownloading(pdf.id);
    try {
      const res = await axios.get(`${API}/api/notes/pdf/stored/${pdf.id}`, {
        params: { user_id: user.id },
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${pdf.name.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(null);
    }
  };

  // Delete stored PDF
  const handleDelete = async (pdfId) => {
    if (!window.confirm("Are you sure you want to delete this PDF?")) return;
    
    setDeleting(pdfId);
    try {
      await axios.delete(`${API}/api/notes/pdf/stored/${pdfId}`, {
        params: { user_id: user.id }
      });
      toast.success("PDF deleted!");
      fetchStoredPDFs();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete PDF");
    } finally {
      setDeleting(null);
    }
  };

  // Toggle topic selection
  const toggleTopic = (topicName) => {
    setSelectedTopics(prev => {
      if (prev.includes(topicName)) {
        return prev.filter(t => t !== topicName);
      }
      if (prev.length >= 20) {
        toast.error("Maximum 20 topics can be merged at once");
        return prev;
      }
      return [...prev, topicName];
    });
  };

  // Check merge readiness and show appropriate dialog
  const handleMergeClick = () => {
    if (selectedTopics.length < 1) {
      toast.error("Select at least 1 topic to merge");
      return;
    }
    
    const readyTopics = availableTopics.filter(t => selectedTopics.includes(t.name) && t.is_ready);
    const notReadyTopics = availableTopics.filter(t => selectedTopics.includes(t.name) && !t.is_ready);
    
    // Calculate estimated time: ~2 min per topic + 1 min for merge
    const estimatedMinutes = notReadyTopics.length * 2 + 1;
    
    setMergePreview({
      total: selectedTopics.length,
      ready: readyTopics.length,
      notReady: notReadyTopics.length,
      notReadyNames: notReadyTopics.map(t => t.name),
      estimatedMinutes
    });
    
    if (notReadyTopics.length > 0) {
      setShowConfirmDialog(true);
    } else {
      executeMerge();
    }
  };

  // Execute merge (immediate or background)
  const executeMerge = async () => {
    setMerging(true);
    setShowConfirmDialog(false);
    
    try {
      const topicsData = selectedTopics.map(topicName => ({
        exam_type: subjectSelection.category,
        subject: subjectSelection.subject,
        topic: topicName
      }));
      
      const res = await axios.post(`${API}/api/notes/pdf/merge-with-generation`, {
        user_id: user.id,
        user_email: user.email || "",
        topics: topicsData,
        medium: selectedMedium,
        name: customName || undefined
      });
      
      if (res.data.immediate) {
        toast.success(`Merged ${res.data.topics_count} topics! PDF saved.`);
        fetchStoredPDFs();
      } else {
        toast.success(`Background generation started! ${res.data.not_ready_topics} topics will be generated. We'll email you when ready.`);
        fetchPendingPDFs();
      }
      
      setShowMergeDialog(false);
      setSelectedTopics([]);
      setCustomName("");
      
    } catch (err) {
      console.error("Merge failed:", err);
      toast.error(err.response?.data?.detail || "Failed to merge PDFs");
    } finally {
      setMerging(false);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (isoDate) => {
    if (!isoDate) return "N/A";
    return new Date(isoDate).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Get selected topics stats
  const getSelectionStats = () => {
    const selected = availableTopics.filter(t => selectedTopics.includes(t.name));
    const ready = selected.filter(t => t.is_ready).length;
    const notReady = selected.length - ready;
    return { total: selected.length, ready, notReady };
  };

  const stats = getSelectionStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <FolderOpen className="w-8 h-8 text-indigo-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Merge PDFs
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="hover:bg-indigo-50">
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subject info banner + Create Merge button */}
        {subjectSelection && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Badge className="bg-white/20 text-white border-white/30">
                  {subjectSelection.categoryLabel}
                </Badge>
                <span className="font-semibold text-lg">{subjectSelection.subjectLabel}</span>
              </div>
              <Button 
                onClick={() => {
                  setShowMergeDialog(true);
                  fetchTopicsWithStatus();
                }}
                className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold"
                data-testid="create-merge-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Merged PDF
              </Button>
            </div>
          </div>
        )}

        {/* Pending PDFs Section */}
        {pendingPDFs.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Generating PDFs
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {pendingPDFs.map((job) => (
                <Card key={job.id} className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid={`pending-pdf-${job.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">{job.name}</h3>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Progress:</span>
                            <span className="font-medium text-amber-700">
                              {job.progress?.ready + job.progress?.generated || 0} / {job.progress?.total || 0} topics
                            </span>
                          </div>
                          <div className="w-full bg-amber-200 rounded-full h-2">
                            <div 
                              className="bg-amber-500 h-2 rounded-full transition-all"
                              style={{ 
                                width: `${((job.progress?.ready + job.progress?.generated || 0) / (job.progress?.total || 1)) * 100}%` 
                              }}
                            />
                          </div>
                          {job.progress?.current_topic && (
                            <p className="text-xs text-amber-600 truncate">
                              Generating: {job.progress.current_topic}
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Estimated: ~{job.estimated_minutes || 5} minutes • You'll receive an email when ready
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Stored PDFs Section */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Saved PDFs</h2>
          <p className="text-gray-500">Merged PDFs are saved here for easy access and printing</p>
          <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-4 py-2 rounded-lg inline-flex">
            <Clock className="w-4 h-4" />
            <span><strong>Note:</strong> Merged PDFs are stored for <strong>2 days</strong> only. Please download and save locally for permanent access.</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : storedPDFs.length === 0 ? (
          <Card className="border-dashed border-2 border-gray-300">
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No PDFs Saved Yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Merge multiple topic notes into a single PDF for easy printing and offline study.
              </p>
              {subjectSelection && (
                <Button 
                  onClick={() => {
                    setShowMergeDialog(true);
                    fetchTopicsWithStatus();
                  }}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Merged PDF
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {storedPDFs.map((pdf) => (
              <Card key={pdf.id} className="hover:shadow-lg transition-shadow" data-testid={`stored-pdf-${pdf.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-start gap-3">
                    <FileText className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-2">{pdf.name}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Topics count */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Badge variant="outline">{pdf.topics_count} topics</Badge>
                    <span className="text-gray-300">•</span>
                    <Badge variant="outline" className="capitalize">
                      <Globe className="w-3 h-3 mr-1" />
                      {pdf.language || "english"}
                    </Badge>
                  </div>
                  
                  {/* Meta info */}
                  <div className="space-y-1 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(pdf.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4" />
                      <span>{formatSize(pdf.size_bytes)}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => handleDownload(pdf)}
                      disabled={downloading === pdf.id}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      data-testid={`download-pdf-${pdf.id}`}
                    >
                      {downloading === pdf.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleDelete(pdf.id)}
                      disabled={deleting === pdf.id}
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-pdf-${pdf.id}`}
                    >
                      {deleting === pdf.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Back button */}
        <div className="mt-8">
          <Link to="/dashboard">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </main>

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-indigo-600" />
              Create Merged PDF
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Medium selector */}
            <div className="mb-4 p-4 bg-indigo-50 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Select Medium</label>
                  <Select value={selectedMedium} onValueChange={setSelectedMedium}>
                    <SelectTrigger className="w-40 bg-white" data-testid="medium-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">
                        <span className="flex items-center gap-2">
                          <span className="text-lg">A</span> English
                        </span>
                      </SelectItem>
                      <SelectItem value="hindi">
                        <span className="flex items-center gap-2">
                          <span className="text-lg">अ</span> Hindi
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Selection summary */}
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-green-700 font-medium">{stats.ready} Ready</span>
                  </div>
                  {stats.notReady > 0 && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-amber-700 font-medium">{stats.notReady} Not Ready</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Custom name input */}
            <div className="mb-4">
              <label className="text-sm text-gray-600 mb-1 block">Custom Name (optional)</label>
              <Input
                placeholder="e.g., Physics Mechanics Complete"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                data-testid="merge-custom-name"
              />
            </div>
            
            {/* Topic selection header */}
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-sm text-gray-600">
                Select topics ({selectedTopics.length}/20)
              </span>
              {selectedTopics.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedTopics([])}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Clear selection
                </Button>
              )}
            </div>
            
            {/* Topics list with status */}
            <div className="flex-1 overflow-y-auto border rounded-lg divide-y">
              {loadingTopics ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-600">Loading topics...</span>
                </div>
              ) : availableTopics.length === 0 ? (
                <div className="py-10 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No topics available for this subject.</p>
                </div>
              ) : (
                availableTopics.map((topic, idx) => {
                  const isSelected = selectedTopics.includes(topic.name);
                  return (
                    <div
                      key={topic.name}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleTopic(topic.name)}
                      data-testid={`merge-topic-${idx}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleTopic(topic.name)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm text-gray-400 w-8">{idx + 1}</span>
                      <span className={`flex-1 truncate ${isSelected ? 'text-indigo-800 font-medium' : 'text-gray-700'}`}>
                        {topic.name}
                      </span>
                      {/* Status badge */}
                      {topic.is_ready ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200 flex-shrink-0">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          READY
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 flex-shrink-0">
                          <Clock className="w-3 h-3 mr-1" />
                          NOT READY
                        </Badge>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Merge button */}
            <div className="mt-4 space-y-3">
              {/* 2-day storage notice */}
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>Merged PDFs will be stored for <strong>2 days only</strong>. Download and save locally for permanent access.</span>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  {selectedTopics.length >= 1 
                    ? `Ready to merge ${selectedTopics.length} topic${selectedTopics.length > 1 ? 's' : ''}`
                    : 'Select at least 1 topic'}
                </p>
                <Button
                  onClick={handleMergeClick}
                  disabled={selectedTopics.length < 1 || merging}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="confirm-merge-btn"
                >
                  {merging ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Merging...
                    </>
                  ) : (
                    <>
                      <Merge className="w-4 h-4 mr-2" />
                      Merge & Save PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Not-Ready Topics */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Some Topics Need Generation
            </DialogTitle>
          </DialogHeader>
          
          {mergePreview && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-gray-700 mb-3">
                  <strong>Dear Student,</strong>
                </p>
                <p className="text-gray-600 mb-3">
                  Since <strong>{mergePreview.notReady}</strong> out of <strong>{mergePreview.total}</strong> topics 
                  are not ready, we will generate them and save the complete PDF in approximately 
                  <strong className="text-amber-700"> {mergePreview.estimatedMinutes} minutes</strong>.
                </p>
                <p className="text-sm text-gray-500">
                  You'll receive an email when your merged PDF is ready.
                </p>
              </div>
              
              {/* Topics breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Ready Topics
                  </span>
                  <span className="font-medium text-green-700">{mergePreview.ready}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-amber-50 rounded">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    Topics to Generate
                  </span>
                  <span className="font-medium text-amber-700">{mergePreview.notReady}</span>
                </div>
              </div>
              
              {/* Not ready topics list */}
              {mergePreview.notReadyNames.length > 0 && (
                <div className="max-h-32 overflow-y-auto border rounded p-2">
                  <p className="text-xs text-gray-500 mb-2">Topics to generate:</p>
                  {mergePreview.notReadyNames.map((name, idx) => (
                    <p key={idx} className="text-sm text-gray-600 truncate">
                      {idx + 1}. {name}
                    </p>
                  ))}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeMerge}
                  disabled={merging}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="confirm-background-merge-btn"
                >
                  {merging ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      Start Generation
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
